import { Box3, Quaternion, Vector3 } from "three";
import type { HistoryDir, SugarStateSlice } from "../../history/SugarObject";
import {
  getStateRootByUuid,
  trackStateRoot,
  untrackStateRoot,
} from "../../history/SugarObject";
import { SugarModel } from "../../loader/SugarModel";
import { SugarModelConfigurator } from "../../loader/SugarModelConfigurator";
import { logConfiguratorSlotState } from "../../util/logConfiguratorSlotState";

/**
 * Parent origin → children AABB center (shared CFG + RD pivot).
 * Clears/realigns kolçaklar so they don't stay at stale locals.
 */
export function centerConfiguratorFromDirectChildren(
  configurator: SugarModelConfigurator,
): void {
  const children = configurator.models;
  if (children.length < 1) {
    configurator.clearSideParts(true);
    return;
  }

  configurator.clearSideParts();

  const bounds = new Box3();
  children.forEach((child) => {
    child.updateMatrixWorld(true);
    bounds.expandByObject(child);
  });
  if (bounds.isEmpty()) return;

  const center = new Vector3();
  bounds.getCenter(center);
  center.y = configurator.position.y;

  const firstQuaternion = new Quaternion();
  children[0].updateMatrixWorld(true);
  children[0].getWorldQuaternion(firstQuaternion);

  children.forEach((child) => child.detach());
  configurator.position.copy(center);
  configurator.quaternion.copy(firstQuaternion);
  configurator.updateMatrixWorld(true);
  children.forEach((child) => configurator.attach(child));
  configurator.recomputeBound();
  configurator.realignSideParts();
}

export interface ConfiguratorCompositionHost {
  objects(): readonly SugarModel[];
  addRoot(model: SugarModel): void;
  removeRoot(model: SugarModel): void;
  createConfigurator(): SugarModelConfigurator;
}

export type ConfiguratorCompositionHostCallbacks =
  ConfiguratorCompositionHost;

export class CallbackConfiguratorCompositionHost
  implements ConfiguratorCompositionHost
{
  constructor(
    private readonly callbacks: ConfiguratorCompositionHostCallbacks,
  ) {}

  objects(): readonly SugarModel[] {
    return this.callbacks.objects();
  }

  addRoot(model: SugarModel): void {
    this.callbacks.addRoot(model);
  }

  removeRoot(model: SugarModel): void {
    this.callbacks.removeRoot(model);
  }

  createConfigurator(): SugarModelConfigurator {
    return this.callbacks.createConfigurator();
  }
}

type JointRef = { modelUuid: string; jointId: number | string };
type ConfiguratorCompositionValue = {
  rootModelUuids: string[];
  configurators: Array<{
    uuid: string;
    parentUuid?: string;
    childUuids: string[];
    bindings: Array<{ first: JointRef; second: JointRef }>;
  }>;
};

/** Configurator üyelik grafiğinin ürünlerden bağımsız mutasyon/state sahibi. */
export class ConfiguratorComposition {
  readonly uuid: string;
  private readonly _known = new Map<string, SugarModelConfigurator>();
  private _applying = false;

  constructor(
    id: string,
    private readonly _host: ConfiguratorCompositionHost,
  ) {
    this.uuid = `configurator-composition:${id}`;
    trackStateRoot(this);
  }

  dispose(): void {
    untrackStateRoot(this);
  }

  clear(): void {
    this._known.clear();
  }

  get state(): SugarStateSlice<ConfiguratorCompositionValue> {
    const configurators = this._activeConfigurators().map(
      ({ configurator, parentUuid }) => ({
        uuid: configurator.uuid,
        parentUuid,
        childUuids: configurator.models.map((model) => model.uuid),
        bindings: captureBindings(compositionModels(configurator)),
      }),
    );
    const rootModelUuids = this._host
      .objects()
      .filter((object) => !(object instanceof SugarModelConfigurator))
      .map((object) => object.uuid);
    return {
      kind: "configuratorComposition",
      value: { rootModelUuids, configurators },
    };
  }

  applyState(slice: SugarStateSlice, _dir: HistoryDir): void {
    if (slice.kind !== "configuratorComposition") return;
    const value = slice.value as Partial<ConfiguratorCompositionValue>;
    this._applying = true;
    try {
      this._restore(value.configurators ?? [], value.rootModelUuids ?? []);
    } finally {
      this._applying = false;
    }
  }

  adopt(configurator: SugarModelConfigurator): SugarModelConfigurator {
    this._known.set(configurator.uuid, configurator);
    return configurator;
  }

  create(models: readonly SugarModel[]): SugarModelConfigurator {
    const configurator = this.adopt(this._host.createConfigurator());
    this._host.addRoot(configurator);
    for (const model of models) {
      this._host.removeRoot(model);
      configurator.addModelWithoutComplete(model);
    }
    configurator.complete();
    centerConfiguratorFromDirectChildren(configurator);
    return configurator;
  }

  attach(
    model: SugarModel,
    configurator: SugarModelConfigurator,
  ): SugarModelConfigurator {
    this.adopt(configurator);
    this._host.removeRoot(model);
    configurator.addModel(model);
    centerConfiguratorFromDirectChildren(configurator);
    return configurator;
  }

  merge(
    first: SugarModelConfigurator,
    second: SugarModelConfigurator,
  ): SugarModelConfigurator {
    const parent = this.adopt(this._host.createConfigurator());
    this._host.removeRoot(first);
    this._host.removeRoot(second);
    this._host.addRoot(parent);
    parent.addModelWithoutComplete(first);
    parent.addModelWithoutComplete(second);
    parent.complete();
    centerConfiguratorFromDirectChildren(parent);
    return parent;
  }

  detach(
    model: SugarModel,
    addAsRoot = true,
  ): SugarModelConfigurator | undefined {
    const configurator =
      model.configurator instanceof SugarModelConfigurator
        ? model.configurator
        : undefined;
    if (!configurator) return undefined;
    this.adopt(configurator);
    configurator.grabModel(model);
    model.configurator = undefined;
    model.detach();
    if (addAsRoot) this._host.addRoot(model);
    if (configurator.models.length < 1) {
      configurator.clearSideParts(true);
      this._host.removeRoot(configurator);
    } else {
      centerConfiguratorFromDirectChildren(configurator);
    }
    const roots = addAsRoot
      ? this._host.objects()
      : [...this._host.objects(), model];
    logConfiguratorSlotState("composition.detach", roots);
    return configurator;
  }

  /**
   * Tek parçası kalan üst seviye configurator'ı dağıtır: parça kökte bağımsız
   * kalır, boş configurator sahneden çıkar. Dağıttıysa true döner.
   */
  dissolveIfSingle(configurator: SugarModelConfigurator): boolean {
    if (configurator.models.length !== 1) return false;
    if (configurator.configurator) return false;

    const last = configurator.models[0];
    const worldPosition = new Vector3();
    const worldQuaternion = new Quaternion();
    last.updateMatrixWorld(true);
    last.getWorldPosition(worldPosition);
    last.getWorldQuaternion(worldQuaternion);

    this.detach(last);

    last.position.copy(worldPosition);
    last.quaternion.copy(worldQuaternion);
    last.updateMatrixWorld(true);
    return true;
  }

  private _restore(
    states: ConfiguratorCompositionValue["configurators"],
    rootModelUuids: readonly string[],
  ): void {
    const desired = new Set(states.map((state) => state.uuid));
    for (const root of [...this._host.objects()]) {
      this._host.removeRoot(root);
    }
    for (const configurator of this._allConfigurators()) {
      for (const model of [...configurator.models]) {
        configurator.grabModel(model);
        model.configurator = undefined;
        model.detach();
      }
      if (!desired.has(configurator.uuid)) {
        this._host.removeRoot(configurator);
      }
    }

    const restored = new Map(
      states.map((state) => [
        state.uuid,
        this._resolveConfigurator(state.uuid),
      ]),
    );
    for (const uuid of rootModelUuids) {
      const model = getStateRootByUuid(uuid);
      if (model instanceof SugarModel) this._host.addRoot(model);
    }
    for (const state of states) {
      const configurator = restored.get(state.uuid)!;
      if (!state.parentUuid) this._host.addRoot(configurator);
      const children = state.childUuids
        .map((uuid) => restored.get(uuid) ?? getStateRootByUuid(uuid))
        .filter((model): model is SugarModel => model instanceof SugarModel);
      for (const child of children) {
        this._host.removeRoot(child);
        configurator.addRootModel(child);
      }
      configurator.recomputeBound();
      configurator.updateMatrixWorld(true);
    }
    for (const state of states) {
      const configurator = restored.get(state.uuid)!;
      restoreBindings(compositionModels(configurator), state.bindings);
    }
  }

  private _resolveConfigurator(uuid: string): SugarModelConfigurator {
    const registered = getStateRootByUuid(uuid);
    if (registered instanceof SugarModelConfigurator) {
      return this.adopt(registered);
    }
    const created = this._host.createConfigurator();
    untrackStateRoot(created);
    (created as { uuid: string }).uuid = uuid;
    trackStateRoot(created);
    return this.adopt(created);
  }

  private _allConfigurators(): SugarModelConfigurator[] {
    for (const object of this._host.objects()) {
      if (object instanceof SugarModelConfigurator) this.adopt(object);
    }
    return [...this._known.values()];
  }

  private _activeConfigurators(): Array<{
    configurator: SugarModelConfigurator;
    parentUuid?: string;
  }> {
    const result: Array<{
      configurator: SugarModelConfigurator;
      parentUuid?: string;
    }> = [];
    const visit = (
      configurator: SugarModelConfigurator,
      parentUuid?: string,
    ): void => {
      if (result.some((entry) => entry.configurator === configurator)) return;
      this.adopt(configurator);
      result.push({ configurator, parentUuid });
      for (const child of configurator.models) {
        if (child instanceof SugarModelConfigurator) {
          visit(child, configurator.uuid);
        }
      }
    };
    for (const object of this._host.objects()) {
      if (object instanceof SugarModelConfigurator) visit(object);
    }
    return result;
  }
}

function captureBindings(
  models: readonly SugarModel[],
): Array<{ first: JointRef; second: JointRef }> {
  const modelIds = new Set(models.map((model) => model.uuid));
  const seen = new Set<string>();
  const result: Array<{ first: JointRef; second: JointRef }> = [];
  for (const model of models) {
    for (const joint of model.joints ?? []) {
      const other = joint.bindedJoint;
      if (!other || !modelIds.has(other.model.uuid)) continue;
      const first = `${model.uuid}:${joint.jointId}`;
      const second = `${other.model.uuid}:${other.jointId}`;
      const key = [first, second].sort().join("|");
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({
        first: { modelUuid: model.uuid, jointId: joint.jointId },
        second: { modelUuid: other.model.uuid, jointId: other.jointId },
      });
    }
  }
  return result;
}

function restoreBindings(
  models: readonly SugarModel[],
  bindings: readonly { first: JointRef; second: JointRef }[],
): void {
  const byUuid = new Map(models.map((model) => [model.uuid, model]));
  for (const binding of bindings) {
    const first = byUuid
      .get(binding.first.modelUuid)
      ?.joints?.find((joint) => joint.jointId === binding.first.jointId);
    const second = byUuid
      .get(binding.second.modelUuid)
      ?.joints?.find((joint) => joint.jointId === binding.second.jointId);
    if (first && second) first.bindJoint(second);
  }
}

function compositionModels(configurator: SugarModelConfigurator): SugarModel[] {
  const result: SugarModel[] = [];
  const visit = (model: SugarModel): void => {
    if (model instanceof SugarModelConfigurator) {
      for (const child of model.models) visit(child);
      return;
    }
    if (!result.includes(model)) result.push(model);
  };
  for (const model of configurator.models) visit(model);
  return result;
}
